import { useContext, useMemo, type Dispatch } from "react";

import { useT } from "@repo/transifex";

import { AppContext, AppDispatchContext } from "./app-provider";
import { getPrinterErrorMessage } from "./printer-error-message";
import type { AppAction, AppState } from "./types";

/**
 * Hook to get the app state
 * @returns {AppState}
 */
export function useAppContext(): AppState {
  return useContext(AppContext);
}

/**
 * Hook to get the dispatch function
 * for sending actions to the app reducer
 * @returns {Dispatch<AppAction>}
 */
export function useAppDispatch(): Dispatch<AppAction> {
  return useContext(AppDispatchContext);
}

export function useCashDrawerStatus() {
  const { hardware } = useAppContext();

  return useMemo(() => {
    return hardware.cashDrawer.status;
  }, [hardware.cashDrawer.status]);
}

export function useDeviceData() {
  const { device } = useAppContext();

  return useMemo(() => {
    return device;
  }, [device]);
}

export function usePrinterStatus() {
  const { hardware } = useAppContext();

  return useMemo(() => {
    return hardware.printing.status;
  }, [hardware.printing.status]);
}

export function usePrinterError() {
  const t = useT();
  const { hardware } = useAppContext();

  return hardware.printerError
    ? getPrinterErrorMessage(hardware.printerError, t)
    : null;
}

export function usePrintingError() {
  const t = useT();
  const { hardware } = useAppContext();

  return hardware.printing.message
    ? getPrinterErrorMessage(hardware.printing.message, t)
    : null;
}
